import type { GQL_ID } from '@@partner:types:graphql'
import type { CrudFormProps, LoyaltyCouponSeriesTranslation } from '@@partner:types:app'
import type { LoyaltyCouponSeriesFormState } from '@@partner:types:app'

import React, { useState, useCallback, useEffect } from 'react'
import { mergeLeft } from 'merge-left-utils'

import { useFetchSingleLoyaltyCouponSeries } from '@@partner:graphql/query/loyaltyCouponSeries/singleLoyaltyCouponSeries.gql'
import { 
  useMutateCreateLoyaltyCouponSeries, 
  useMutateUpdateLoyaltyCouponSeries 
} from '@@partner:graphql/mutation/loyaltyCouponSeries/saveLoyaltyCouponSeries.gql'

import {
  makeLoyaltyCouponSeriesFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './LoyaltyCouponSeriesFormState'

import { LoyaltyCouponSeriesFormValidationModel } from './validation/LoyaltyCouponSeriesFormValidationModel'

import {
  Form,
  Input,
  InputGroup,
  Panel,
  Grid,
  Row,
  Col,
  DatePicker,
  Divider
} from 'rsuite'

import { SaveButton } from '@components/common/form/Buttons'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import FormTextareaControl from '@components/common/form/FormTextareaControl'
import LocalizedForm from '@components/common/localizedForm/LoclalizedForm'
import SelectLocale from '@components/common/localizedForm/SelectLocale'
import FormCheckbox from '@components/common/form/FormCheckbox'
// import FileUploaderField from '@components/common/form/FileUploaderField'


import {
  notifyOk,
  makeGraphQLErrorNotification,
  notifyError
} from '@@partner:helpers/notification'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'

export interface LoyaltyCouponSeriesFormProps extends CrudFormProps {
  couponSeriesId?: GQL_ID
}

export default function LoyaltyCouponSeriesForm ({
  couponSeriesId,
  onSaved
}: LoyaltyCouponSeriesFormProps) {
  const [ formError, setFormError ] = useState({})
  const [ selectedLanguage, setSelectedLanguage ] = useState<string>('По-умолчанию')
  const { formState, setFormState, setFormRef } =
    useValidatableForm<LoyaltyCouponSeriesFormState>(makeLoyaltyCouponSeriesFormState)

  const {
    data,
    loading: loadingCouponSeries,
    error: errorLoadingCouponSeries
  } = useFetchSingleLoyaltyCouponSeries({
    id: couponSeriesId!
  })

  const [ createLoyaltyCouponSeries, { loading: loadingCreate } ] =
    useMutateCreateLoyaltyCouponSeries()
  
  const [ updateLoyaltyCouponSeries, { loading: loadingUpdate } ] =
    useMutateUpdateLoyaltyCouponSeries()

  const isEditing = !!couponSeriesId
  const loading = loadingCreate || loadingUpdate

  // Получаем список доступных языков из переводов
  const availableLanguages = [ 'По-умолчанию', ...formState.i18n.map(translation => translation.language_code) ]

  // Получаем данные для выбранного языка
  const selectedTranslation = selectedLanguage === 'По-умолчанию' 
    ? null 
    : formState.i18n.find(translation => translation.language_code === selectedLanguage)

  const currentTitle = selectedLanguage === 'По-умолчанию' 
    ? formState.title 
    : selectedTranslation?.title || ''

  const currentDescription = selectedLanguage === 'По-умолчанию' 
    ? formState.description 
    : selectedTranslation?.description || ''

  useEffect(() => {
    if (!loadingCouponSeries && data) {
      setFormState((state) =>
        mergeLeft(state, mapApiDataToState(data?.loyaltyCouponSeries))
      )
    }
  }, [ data, loadingCouponSeries, setFormState ])

  const handleFormError = useCallback((error: any) => {
    setFormError(error)
  }, [])

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  // Обработчик добавления нового языка
  const handleAddLanguage = useCallback((languageCode: string) => {
    setFormState(state => ({
      ...state,
      i18n: [ ...state.i18n, { 
        language_code: languageCode, 
        title: '', 
        description: '' 
      } ]
    }))
    setSelectedLanguage(languageCode)
  }, [ setFormState ])

  // Обработчик изменения значений перевода
  const handleTranslationChange = useCallback(
    (field: keyof LoyaltyCouponSeriesTranslation) => (value: string) => {
      if (selectedLanguage === 'По-умолчанию') {
        setFormState(state => ({ ...state, [field]: value }))
      }
      else {
        setFormState(state => ({
          ...state,
          i18n: state.i18n.map(translation => 
            translation.language_code === selectedLanguage
              ? { ...translation, [field]: value }
              : translation
          )
        }))
      }
    }, [ selectedLanguage, setFormState ])

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        const apiData = mapStateToApiVariable(formState)
        
        const mutation = isEditing && apiData.id
          ? updateLoyaltyCouponSeries({ 
            variables: { 
              id: apiData.id, 
              input: apiData.input 
            } 
          })
          : createLoyaltyCouponSeries({ 
            variables: { 
              input: apiData.input 
            } 
          })

        mutation
          .then(({ data }) => {
            notifyOk(isEditing ? 'Серия купонов обновлена' : 'Серия купонов создана')
            onSaved?.(data?.loyaltyCouponSeries.id)
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ formState, isEditing, createLoyaltyCouponSeries, updateLoyaltyCouponSeries, onSaved ]
  )

  if (errorLoadingCouponSeries) {
    return <GraphQLCommonErrorDisplay error={errorLoadingCouponSeries} />
  }

  return (
    <Form
      ref={setFormRef}
      model={LoyaltyCouponSeriesFormValidationModel}
      formValue={formState}
      formError={formError}
      onCheck={handleFormError}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      checkTrigger="blur"
      fluid
    >
      <Grid fluid>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Panel header="Основная информация" bordered>
              <Form.Group>
                <Form.ControlLabel>Код серии *</Form.ControlLabel>
                <Form.Control
                  name="code"
                  accepter={Input}
                  placeholder="Введите уникальный код серии"
                  style={{ textTransform: 'uppercase' }}
                />
                <Form.HelpText>
                  Уникальный код серии купонов (только заглавные буквы, цифры и _)
                </Form.HelpText>
              </Form.Group>

              {/* Локализация */}
              <Form.Group>
                <Form.ControlLabel>Языки</Form.ControlLabel>
                <SelectLocale
                  languages={availableLanguages}
                  selectedLanguage={selectedLanguage}
                  onSelect={setSelectedLanguage}
                  onAdd={handleAddLanguage}
                />
              </Form.Group>

              <LocalizedForm
                selectedLanguage={selectedLanguage}
                name="title"
                label="Название серии"
                value={currentTitle ?? ''}
                onChange={handleTranslationChange('title')}
              />

              <LocalizedForm
                selectedLanguage={selectedLanguage}
                name="description"
                label="Описание"
                type="textarea"
                value={currentDescription ?? ''}
                onChange={handleTranslationChange('description')}
                rows={4}
              />
            </Panel>
          </Col>

          <Col xs={24} md={12}>
            <Panel header="Период действия" bordered>
              <Form.Group>
                <Form.ControlLabel>Действует с</Form.ControlLabel>
                <Form.Control
                  name="validFrom"
                  accepter={DatePicker}
                  block
                  oneTap
                  isoWeek
                  format="dd.MM.yyyy HH:mm"
                  placeholder="Выберите дату начала"
                  style={{ width: '100%' }}
                  value={formState.validFrom}
                />
              </Form.Group>

              <Form.Group>
                <Form.ControlLabel>Действует до</Form.ControlLabel>
                <Form.Control
                  name="validUntil"
                  accepter={DatePicker}
                  format="dd.MM.yyyy HH:mm"
                  block
                  oneTap
                  isoWeek
                  placeholder="Выберите дату окончания"
                  style={{ width: '100%' }}
                />
              </Form.Group>
            </Panel>
            <Panel header='Прочие настройки'>
              <Form.Group>
                <Form.ControlLabel>Может быть активирован</Form.ControlLabel>
                <FormCheckbox.Control name='canBeActivated' />
              </Form.Group>
            </Panel>

            {/* <Panel header="Изображение" bordered>
              <Form.Group>
                <Form.ControlLabel>Изображение серии</Form.ControlLabel>
                <FileUploaderField
                  name="image_file_id"
                  accept="image/*"
                  maxFileSize={5 * 1024 * 1024}
                  helpText="Изображение для отображения серии купонов"
                />
              </Form.Group>
            </Panel> */}
          </Col>
        </Row>

        <Divider />

        <Row>
          <Col xs={24}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <SaveButton
                type="submit"
                loading={loading || loadingCouponSeries}
                disabled={loading || loadingCouponSeries}
              >
                {isEditing ? 'Обновить' : 'Создать'}
              </SaveButton>
            </div>
          </Col>
        </Row>
      </Grid>
    </Form>
  )
}
